import React from 'react';
import topLevelDomains from 'tlds';

const domainName = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;
const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const looseEmailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PASSWORD_SPECIAL_CHARACTERS = "!@#$%^&*()-_+={}[]|`\":;'<>.,?~\\";
const escapeForRegex = (value: string) =>
  value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
const passwordSpecialCharClass = escapeForRegex(PASSWORD_SPECIAL_CHARACTERS);
const passwordRegex = new RegExp(
  '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[' +
    passwordSpecialCharClass +
    '])(?!.* ).{8,}$'
);
const passwordRegexFooda = new RegExp(
  '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[' +
    passwordSpecialCharClass +
    '])(?!.* ).{12,}$'
);
export const PASSWORD_REQUIREMENTS_MESSAGE = `Password must be at least 8 characters long (12 for @fooda.com), include uppercase and lowercase letters, a number, and one of these characters: ${PASSWORD_SPECIAL_CHARACTERS}`;
export const PASSWORD_REQUIREMENTS_MESSAGE_SIMPLE = `Password must be at least 8 characters long, include uppercase and lowercase letters, a number, and one of these characters: ${PASSWORD_SPECIAL_CHARACTERS}`;
export const PASSWORD_REQUIREMENTS_MESSAGE_FOODA = `Password must be at least 12 characters long, include uppercase and lowercase letters, a number, and one of these characters: ${PASSWORD_SPECIAL_CHARACTERS}`;
const FOODA_DOMAIN = '@fooda.com';

type Validator = (value: unknown, values: Record<string, unknown>) => React.ReactNode | null;

export const combineValidators =
  (...validators: Validator[]) =>
  (...values: [unknown, Record<string, unknown>]) =>
    validators.find((validator) => validator(...values))?.(...values) ?? null;

export const isDomainName = (error?: React.ReactNode) => {
  const _error = error || true;

  return (value: unknown) => {
    if (typeof value !== 'string') {
      return _error;
    }

    if (!domainName.test(value)) {
      return _error;
    }

    const domain = value.split('.').pop() || '';

    return topLevelDomains.includes(domain) ? null : _error;
  };
};

export const isStrongPassword = (error?: React.ReactNode) => {
  const defaultError =
    error ||
    PASSWORD_REQUIREMENTS_MESSAGE;

  return (value: unknown, values: Record<string, unknown>) => {
    if (typeof value !== 'string') {
      return defaultError;
    }

    const email = values?.email;
    const isFoodaEmail =
      typeof email === 'string' && email.toLowerCase().trim().endsWith(FOODA_DOMAIN);
    const regex = isFoodaEmail ? passwordRegexFooda : passwordRegex;

    return regex.test(value) ? null : defaultError;
  };
};

export const isEmailAddress = (error?: React.ReactNode) => {
  const _error = error || true;

  return (value: unknown) => {
    if (typeof value !== 'string') {
      return _error;
    }

    return emailRegex.test(value.trim()) ? null : _error;
  };
};

export const looksLikeEmail = (error?: React.ReactNode) => {
  const _error = error || true;

  return (value: unknown) => {
    if (typeof value !== 'string' || value.trim().length === 0) {
      return _error;
    }

    return looseEmailRegex.test(value.trim()) ? null : _error;
  };
};